/**
 * Interactive widgets embedded in blog articles (article.content).
 *
 * Shopify's rich-text editor strips <script> tags and heading ids from the
 * HTML pasted into articles, so the behaviour for those widgets lives here:
 *   1. Table of contents  - gives headings the ids the TOC links point to.
 *   2. Checklists         - `.kc-cl` (floating bottle) and `.kcp` (inline bottle).
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ *
   * 1. Table of contents anchors
   * ------------------------------------------------------------------ */
  function normalize(text) {
    return (text || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  }

  function initTocAnchors() {
    var content = document.querySelector('.article-content');
    if (!content) return;

    var links = Array.prototype.filter.call(content.querySelectorAll('a[href^="#"]'), function (a) {
      return a.getAttribute('href').length > 1;
    });
    if (!links.length) return;

    var tocList = links[0].closest('nav, ul, ol');
    var headings = Array.prototype.slice.call(content.querySelectorAll('h2, h3, h4'))
      .map(function (h) { return { el: h, text: normalize(h.textContent) }; })
      .filter(function (h) { return h.text; });

    // Translated articles have the TOC and the headings translated separately, so the
    // texts don't match. The TOC lists the h2 sections in order: map them by position.
    var sections = headings.filter(function (h) {
      return h.el.tagName === 'H2' && tocList &&
        (tocList.compareDocumentPosition(h.el) & Node.DOCUMENT_POSITION_FOLLOWING) &&
        !tocList.contains(h.el);
    });
    var byPosition = sections.length === links.length;

    links.forEach(function (link, index) {
      var id = decodeURIComponent(link.getAttribute('href').slice(1));
      if (document.getElementById(id)) return;

      var match = byPosition ? sections[index] : null;
      if (!match) {
        var label = normalize(link.textContent);
        match = headings.filter(function (h) { return !h.el.id && h.text === label; })[0] ||
          headings.filter(function (h) {
            return !h.el.id && label.length > 8 && h.text.length > 8 &&
              (h.text.indexOf(label) === 0 || label.indexOf(h.text) === 0);
          })[0];
      }

      if (match && !match.el.id) match.el.id = id;
    });

    if (location.hash.length > 1) {
      var target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (target) target.scrollIntoView();
    }
  }

  /* ------------------------------------------------------------------ *
   * 2. Checklists
   * ------------------------------------------------------------------ */
  var BOTTLE_BOTTOM = 257; // inner bottle: liquid rect starts here...
  var BOTTLE_TOP = 113;    // ...and fills up to here (viewBox 0 0 140 280)
  var MOBILE_BOTTOM = 260;

  var VARIANTS = [
    {
      root: '.kc-cl',
      item: '.kc-cl__item',
      path: '.kc-cl__path',
      header: '.kc-cl__path-header',
      counter: '.kc-cl__path-counter',
      bar: '.kc-cl__path-bar-inner',
      reset: '.kc-cl__reset',
      checked: 'kc-checked',
      collapsed: 'kc-collapsed',
      visible: 'kc-visible',
      badge: '#kc-done-badge',
      pct: ['#kc-pct', '#kc-mobile-pct'],
      fill: '#kc-liquid-fill',
      wave: '#kc-wave',
      sheen: '#kc-sheen-el',
      mobileFill: '#kc-m-fill',
      float: { layout: '.kc-cl__layout', col: '#kc-bottle-col', panel: '#kc-bottle-panel' }
    },
    {
      // Static prep lists (no progress / bottle): only ticking steps.
      root: '.krf',
      item: '.krf__step',
      path: '.krf__path',
      checked: 'krf-checked',
      pct: []
    },
    {
      root: '.kcp',
      item: '.kcp__item',
      path: '.kcp__path',
      header: '.kcp__path-header',
      counter: '.kcp__path-counter',
      bar: '.kcp__path-bar-inner',
      reset: '.kcp__reset',
      checked: 'kcp-checked',
      collapsed: 'kcp-collapsed',
      visible: 'kcp-visible',
      badge: '#kcp-done-badge',
      pct: ['#kcp-pct'],
      totalBar: '#kcp-bar-total',
      fill: '#kcp-liq-fill',
      wave: '#kcp-liq-wave'
    }
  ];

  function qs(sel) { return sel ? document.querySelector(sel) : null; }

  function setLiquid(v, fraction) {
    var fill = qs(v.fill);
    if (fill) {
      var h = (BOTTLE_BOTTOM - BOTTLE_TOP) * fraction;
      fill.setAttribute('y', BOTTLE_BOTTOM - h);
      fill.setAttribute('height', h);
      var wave = qs(v.wave);
      if (wave) {
        wave.setAttribute('cy', BOTTLE_BOTTOM - h);
        wave.setAttribute('opacity', fraction > 0 && fraction < 1 ? 1 : 0);
      }
      var sheen = qs(v.sheen);
      if (sheen) {
        sheen.setAttribute('y', BOTTLE_BOTTOM - h);
        sheen.setAttribute('opacity', fraction > 0 ? 1 : 0);
      }
    }
    var mobile = qs(v.mobileFill);
    if (mobile) {
      var mh = (MOBILE_BOTTOM - BOTTLE_TOP) * fraction;
      mobile.setAttribute('y', MOBILE_BOTTOM - mh);
      mobile.setAttribute('height', mh);
    }
  }

  function render(v, fraction) {
    setLiquid(v, fraction);
    var label = Math.round(fraction * 100) + '%';
    v.pct.forEach(function (sel) {
      var el = qs(sel);
      if (el) el.textContent = label;
    });
    var total = qs(v.totalBar);
    if (total) total.style.width = label;
  }

  function makeAnimator(v) {
    var shown = 0;
    var frame = null;
    return function animateTo(target) {
      if (frame) cancelAnimationFrame(frame);
      if (reduceMotion) {
        shown = target;
        render(v, shown);
        return;
      }
      var from = shown;
      var start = null;
      (function step(now) {
        if (start === null) start = now;
        var t = Math.min((now - start) / 450, 1);
        var eased = 1 - Math.pow(1 - t, 3);
        shown = from + (target - from) * eased;
        render(v, shown);
        if (t < 1) frame = requestAnimationFrame(step);
      })(performance.now());
    };
  }

  // Keeps the bottle panel next to the checklist while scrolling (desktop only).
  function initFloatingPanel(f) {
    var layout = qs(f.layout);
    var col = qs(f.col);
    var panel = qs(f.panel);
    if (!layout || !col || !panel) return;

    document.body.appendChild(panel);
    panel.style.position = 'fixed';
    panel.style.zIndex = '40';

    function place() {
      if (window.innerWidth <= 680) return;
      var lr = layout.getBoundingClientRect();
      var cr = col.getBoundingClientRect();
      var top = Math.min(Math.max(96, lr.top), lr.bottom - panel.offsetHeight);
      panel.style.left = (cr.left + (cr.width - panel.offsetWidth) / 2) + 'px';
      panel.style.top = top + 'px';
      panel.style.visibility = lr.bottom > 0 && lr.top < window.innerHeight ? 'visible' : 'hidden';
    }

    place();
    window.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place);
  }

  // Some articles still carry their own inline <script> for the widget (it survives
  // when pasted through some editors). Two handlers on the same items would toggle
  // each click twice and cancel out, so leave those widgets to their own script.
  function hasOwnScript(root) {
    if (!root.id) return false;
    var scope = root.closest('.article-content') || document;
    return Array.prototype.some.call(scope.querySelectorAll('script:not([src])'), function (script) {
      return script.textContent.indexOf(root.id) !== -1;
    });
  }

  function initChecklist(v, root) {
    if (root.dataset.checklistReady || hasOwnScript(root)) return;
    root.dataset.checklistReady = 'true';

    var paths = Array.prototype.slice.call(root.querySelectorAll(v.path));
    var animateTo = makeAnimator(v);

    function update() {
      var best = 0;
      paths.forEach(function (path) {
        var items = path.querySelectorAll(v.item);
        var done = path.querySelectorAll(v.item + '.' + v.checked).length;
        var fraction = items.length ? done / items.length : 0;
        best = Math.max(best, fraction);

        var counter = v.counter && path.querySelector(v.counter);
        if (counter) counter.textContent = done + ' / ' + items.length;
        var bar = v.bar && path.querySelector(v.bar);
        if (bar) bar.style.width = Math.round(fraction * 100) + '%';
      });

      // The paths are alternatives, so the smoothie is as ready as the furthest one.
      animateTo(best);
      var badge = qs(v.badge);
      if (badge) badge.classList.toggle(v.visible, best === 1);
    }

    function toggleItem(item) {
      var on = item.classList.toggle(v.checked);
      item.setAttribute('aria-checked', on ? 'true' : 'false');
      update();
    }

    root.querySelectorAll(v.item).forEach(function (item) {
      item.setAttribute('role', 'checkbox');
      item.setAttribute('aria-checked', item.classList.contains(v.checked) ? 'true' : 'false');
      item.tabIndex = 0;
      item.addEventListener('click', function () { toggleItem(item); });
      item.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggleItem(item);
        }
      });
    });

    paths.forEach(function (path) {
      var header = v.header && path.querySelector(v.header);
      if (!header) return;
      header.setAttribute('role', 'button');
      header.setAttribute('aria-expanded', path.classList.contains(v.collapsed) ? 'false' : 'true');
      header.tabIndex = 0;
      header.style.cursor = 'pointer';
      function toggle() {
        var collapsed = path.classList.toggle(v.collapsed);
        header.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      }
      header.addEventListener('click', toggle);
      header.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle();
        }
      });
    });

    // The reset button can live inside the floating panel, outside the root.
    var reset = v.reset && (qs(v.reset) || root.querySelector(v.reset));
    if (reset) {
      reset.addEventListener('click', function () {
        root.querySelectorAll(v.item + '.' + v.checked).forEach(function (item) {
          item.classList.remove(v.checked);
          item.setAttribute('aria-checked', 'false');
        });
        update();
      });
    }

    if (v.float) initFloatingPanel(v.float);
    update();
  }

  function initChecklists() {
    VARIANTS.forEach(function (v) {
      var root = document.querySelector('.article-content ' + v.root) || document.querySelector(v.root);
      if (root) initChecklist(v, root);
    });
  }

  function init() {
    initTocAnchors();
    initChecklists();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
